import { IconButton, toast } from "@carbon6/c6-frontend-design";
import { useEffect, useMemo, useRef, useState } from "react";
import { reprocessStaleUploadJob } from "@/api";
import { useSearchParams } from "react-router";
import { useAdminTable } from "@/app/useAdminTable";
import { parsePageParam } from "@/utils";
import { PaginationFooter, ResourceBrowserLayout } from "@/components/browse";
import { FilterResultCount } from "@/components/filter";
import { BulkActionBar } from "@/components/table/BulkActionBar";
import bulkActionBarStyles from "@/components/table/BulkActionBar.module.css";
import { HatDataTable } from "@/components/table/HatDataTable";
import { isHttpClientError } from "@/http";
import { HatTextModal } from "@/components/modal";
import { openDatasetDetailWindow } from "@/app/datasetDetailWindow";
import { resolveErrorMessage } from "@/page/datasetUpload/datasetUploadFormatters";
import { shortUuid } from "./adminStorageFormatters";
import { createUploadRecoveryColumns } from "./uploadRecoveryColumns";
import { UploadRecoveryTableSource } from "./UploadRecoveryTableSource";
import { tableStripError } from "@/components/table/tableStateMessages";

// 재큐 확인 대상. label 은 확인 문구용 표시명.
type ReprocessTarget = {
  uploadUuid: string;
  label: string;
};

// 멈춘 작업과 취소 Dataset을 한 목록에 표시하고 멈춘 작업만 재큐한다.
export function UploadRecoveryPanel() {
  const [params, setParams] = useSearchParams();
  const page = parsePageParam(params.get("page"));
  const source = useMemo(
    () =>
      new UploadRecoveryTableSource({
        initialPage: page,
        initialPageSize: 20,
        resolveErrorMessage,
      }),
    [page],
  );
  const snapshot = useAdminTable(source, false);
  const [confirmTarget, setConfirmTarget] = useState<ReprocessTarget | null>(null);
  const [confirmBusy, setConfirmBusy] = useState(false);

  // 언마운트 후 도착한 POST 응답이 상태를 바꾸지 않게 하는 가드(StrictMode 재마운트 시 복구).
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const columns = useMemo(
    () =>
      createUploadRecoveryColumns({
        onOpenDataset: (datasetUuid) =>
          openDatasetDetailWindow({ datasetUuid, displayName: shortUuid(datasetUuid) }),
        onReprocess: (row) => setConfirmTarget({ uploadUuid: row.uuid, label: row.displayName }),
      }),
    [],
  );

  const handleConfirm = (): void => {
    if (confirmTarget === null || confirmBusy) {
      return;
    }
    setConfirmBusy(true);
    void reprocessStaleUploadJob(confirmTarget.uploadUuid)
      .then(() => {
        if (!aliveRef.current) return;
        toast.success("재큐했습니다. 진행은 데이터 업로드 현황에서 확인해 주세요.");
        // 재큐된 잡은 더 이상 stale 이 아니다. 그래서 목록에서 제거한다.
        void source.refresh();
        if (aliveRef.current) {
          setConfirmBusy(false);
          setConfirmTarget(null);
        }
      })
      .catch((error: unknown) => {
        if (!aliveRef.current) return;
        toast.error(resolveReprocessErrorMessage(error));
        // 409 는 서버가 락 안에서 다시 검증한 결과다. 목록 자체가 오래되었다는 뜻이다. 그래서
        // 다시 읽는다.
        void source.refresh();
        if (aliveRef.current) {
          setConfirmBusy(false);
          setConfirmTarget(null);
        }
      });
  };

  const isLoading = snapshot.loadState === "loading";

  return (
    <>
      <ResourceBrowserLayout
        ariaLabel="멈춘/취소된 업로드 목록"
        summaryCount={snapshot.total}
        summaryLabel="멈춘/취소된 업로드"
        summaryInFilter
        error={tableStripError(snapshot)}
        onRetry={() => void source.refresh()}
        footer={
          <PaginationFooter
            page={page}
            size={snapshot.size}
            total={snapshot.total}
            isLoading={isLoading}
            ariaLabel="멈춘/취소된 업로드 페이지 이동"
            onPrevious={() => snapshot.setPage(page - 1)}
            onNext={() => snapshot.setPage(page + 1)}
          />
        }
      >
        <div className={bulkActionBarStyles.host}>
          <BulkActionBar
            source={source}
            idleContent={<FilterResultCount count={snapshot.total} unitLabel="업로드" />}
            rightContent={
              <>
                <IconButton
                  text="스토리지 회수"
                  size="sm"
                  variant="surface"
                  onClick={() => setParams({ tab: "cleanup", status: "cancelled" })}
                />
                <IconButton
                  text={isLoading ? "로딩 중…" : "새로고침"}
                  size="sm"
                  variant="surface"
                  disabled={isLoading}
                  onClick={() => void source.refresh()}
                />
              </>
            }
            ariaLabel="멈춘/취소된 업로드 목록 도구"
          />
          <HatDataTable
            source={source}
            columns={columns}
            emptyMessage="멈춘/취소된 업로드가 없습니다."
            loadingMessage="멈춘/취소된 업로드를 불러오고 있습니다."
            ariaLabel="멈춘/취소된 업로드 테이블"
            scrollable
            minWidth="1080px"
          />
        </div>
      </ResourceBrowserLayout>
      <HatTextModal
        open={confirmTarget !== null}
        label="업로드 처리를 다시 대기열에 넣습니다."
        eyebrow={confirmTarget?.label}
        caption="업로드한 파일을 재사용합니다. Manifest가 확정된 잡은 보존된 진행 위치부터 이어가며, 미확정 잡은 파일 수집부터 다시 시작합니다."
        okText="재큐"
        busy={confirmBusy}
        onOkPress={handleConfirm}
        onCancelPress={() => setConfirmTarget(null)}
      />
    </>
  );
}

/** 재큐 실패 문구. 계약된 404/409 코드는 원인을 구체적으로 안내한다. */
function resolveReprocessErrorMessage(error: unknown): string {
  if (isHttpClientError(error)) {
    switch (error.code) {
      case "upload.not_found":
        return "업로드 잡을 찾을 수 없습니다.";
      case "upload.reprocess.invalid_status":
        return "이미 상태가 바뀌어 재큐할 수 없습니다. 목록을 새로 읽었습니다.";
      case "upload.reprocess.worker_active":
        return "아직 워커가 처리 중입니다. 잠시 후 다시 확인해 주세요.";
    }
  }
  return error instanceof Error ? error.message : "재큐 요청을 완료하지 못했습니다.";
}
